import { ArrowPathIcon, LockClosedIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';

import { ApiError } from '../api/client';
import { Button } from './Button';
import { Callout } from './Callout';

export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return (
    <ArrowPathIcon className={`animate-spin text-muted ${className}`} aria-hidden="true" />
  );
}

export function LoadingBlock({ label = 'Loading' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-center gap-2 py-10 text-sm text-muted"
    >
      <Spinner />
      {label}…
    </div>
  );
}

export function EmptyState({
  title,
  detail,
  icon: Icon = NoSymbolIcon,
}: {
  title: string;
  detail?: string;
  icon?: typeof NoSymbolIcon;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <Icon className="h-8 w-8 text-subtle" aria-hidden="true" />
      <p className="text-sm font-medium text-body">{title}</p>
      {detail ? <p className="max-w-md text-xs text-subtle">{detail}</p> : null}
    </div>
  );
}

/**
 * Error rendering that distinguishes the three cases a user can act on differently.
 *
 * A 403 is not a failure to recover from — it is a statement about the caller's Entra app
 * role, and offering "Try again" against it is misleading. The backend's `Forbidden` message
 * names the role held and the role required, so it is shown rather than replaced.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (error instanceof ApiError && error.isForbidden) {
    return (
      <Callout tone="warning" role="alert">
        <div className="flex items-start gap-2">
          <LockClosedIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">Not permitted</p>
            <p className="mt-0.5 opacity-90">{error.message}</p>
            <p className="mt-1 text-xs opacity-75">
              App roles are assigned on the API app registration in Entra ID. Ask an
              administrator to grant the role above.
            </p>
          </div>
        </div>
      </Callout>
    );
  }

  const message = error instanceof Error ? error.message : String(error);
  const transient = error instanceof ApiError && error.isTransient;

  return (
    <Callout
      tone="danger"
      role="alert"
      action={
        onRetry ? (
          <Button variant="secondary" size="xs" onClick={onRetry}>
            Try again
          </Button>
        ) : undefined
      }
    >
      <p className="font-medium">{transient ? 'The API did not respond' : 'Request failed'}</p>
      <p className="mt-0.5 opacity-90">{message}</p>
    </Callout>
  );
}

/** Wraps a query's three states so pages do not each re-implement the ladder. */
export function QueryBoundary({
  isLoading,
  error,
  onRetry,
  loadingLabel,
  children,
}: {
  isLoading: boolean;
  error: unknown;
  onRetry?: () => void;
  loadingLabel?: string;
  children: ReactNode;
}) {
  if (isLoading) return <LoadingBlock label={loadingLabel} />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  return <>{children}</>;
}
